import { getNotifications } from "@/dal/get-notifications";
import { useNavigationError } from "@/hooks/useNavigationError";
import type { Notification } from "@/types/notification";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [offset, setOffset] = useState(0);
  const [prevOffset, setPrevOffset] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { navigateError } = useNavigationError();

  const handleNotificationError = (error: any) => {
    if (axios.isAxiosError(error)) {
      navigateError({
        error: error,
        endpoint: "/api/notifications",
        path: "notifications",
        state: {
          status: "RELOAD",
        },
      });
    }
  };

  // 初回読み込み時
  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const { notifications } = await getNotifications({ offset: 0, count: 10 });
        if (notifications.length === 0) {
          return;
        }
        setNotifications(notifications);
        setOffset(5);
      } catch (error) {
        handleNotificationError(error);
      }
    };
    fetchInitial();
  }, []);

  // スクロール時
  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    const fetchMore = async (currentOffset: number) => {
      try {
        const { notifications } = await getNotifications({
          offset: currentOffset,
          count: 5,
        });
        if (notifications.length === 0) {
          return;
        }
        setNotifications((prevNotifications) => {
          const combined = [...prevNotifications, ...notifications];

          // 重複を削除
          const unique = combined.reduce((acc: Notification[], current: Notification) => {
            const x = acc.find((item) => item.id === current.id);
            if (!x) {
              return acc.concat([current]);
            } else {
              return acc;
            }
          }, []);

          return unique;
        });
        setOffset((prevOffset) => prevOffset + 5);
      } catch (error) {
        handleNotificationError(error);
      }
    };

    // Intersection Observerのインスタンスを作成
    const observer = new IntersectionObserver(
      (entries) => {
        // 画面の一番下の要素が交差したかどうかを判定
        if (entries[0].isIntersecting) {
          // 交差したらfetchを呼び出す
          const currentOffset = offset;
          if (currentOffset - prevOffset === 5) {
            fetchMore(currentOffset);
            setPrevOffset(currentOffset);
          }
        }
      },
      {
        // 交差判定の閾値を0に設定
        threshold: 0,
      }
    );

    // 画面の一番下の要素を監視対象に登録
    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    // コンポーネントがアンマウントされたら監視を解除
    return () => observer.disconnect();
  }, [offset]);

  return { notifications, bottomRef };
};
